import sortBy from 'lodash/sortBy';

export const RESULTS_LIMIT = 50;

export const ROLE_ADMIN = 'Admin';
export const ROLE_STANDARD = 'Standard';
export const ROLE_RESTRICTED = 'Restricted';

export const SUSPENDED_EMAIL_PREFIX = '__suspended__';

export const TIER_PARTNER = 'PARTNER';
export const TIER_AGENCY = 'AGENCY';
export const TIER_STANDARD_ANALYTICS = 'STANDARD_ANALYTICS';

export const LABELLED_TIERS = [
    'FREE',
    TIER_STANDARD_ANALYTICS,
    'STANDARD_UNIFIED',
    'ENTERPRISE',
    TIER_PARTNER,
    TIER_AGENCY,
];

export const DEFAULT_FILTERS = {
    tierfilter: '',
    rolefilter: '',
    showAnalyticsOnly: false,
    showDevAccounts: false,
};

export const MATCH_ORG_ID_EXACT = 0;
export const MATCH_ORG_PREFIX = 1;
export const MATCH_ORG_CONTAINS = 2;
export const MATCH_OTHER = 3;

const ROLE_RANKS = { [ROLE_ADMIN]: 0, [ROLE_STANDARD]: 1, [ROLE_RESTRICTED]: 2 };
const ROLE_RANK_OTHER = 3;

export function isSuspended(user) {
    return String((user && user.email) || '').startsWith(SUSPENDED_EMAIL_PREFIX);
}

export function getTierKey(user) {
    if (user.is_partner) {
        return TIER_PARTNER;
    }
    if (user.is_agency) {
        return TIER_AGENCY;
    }
    return user.organization_tier;
}

export function deriveTiers(users) {
    const keys = [...new Set(users.map(user => getTierKey(user)))].filter(Boolean);
    const tiers = keys.filter(key => key !== TIER_PARTNER && key !== TIER_AGENCY).sort();
    return [...tiers, ...[TIER_PARTNER, TIER_AGENCY].filter(key => keys.includes(key))];
}

export function getRoleKey(user) {
    return user.role_type || ROLE_ADMIN;
}

export function roleRank(role) {
    const rank = ROLE_RANKS[role];
    return rank === undefined ? ROLE_RANK_OTHER : rank;
}

export function orgMatchRank(user, upperTerm) {
    const orgId = user.organization_id;
    if (orgId !== null && orgId !== undefined && String(orgId).toUpperCase() === upperTerm) {
        return MATCH_ORG_ID_EXACT;
    }
    const names = [user.organization, user.organization_internal_name]
        .filter(Boolean)
        .map(name => String(name).toUpperCase());
    if (names.some(name => name.startsWith(upperTerm))) {
        return MATCH_ORG_PREFIX;
    }
    if (names.some(name => name.includes(upperTerm))) {
        return MATCH_ORG_CONTAINS;
    }
    return MATCH_OTHER;
}

export function mapUserToOption(user, matchIndex, matchRank = MATCH_OTHER, orgCount = 1) {
    const role = getRoleKey(user);
    let planSuffix = '';
    if (user.is_midmarket) {
        planSuffix += ' · MidMarket';
    }
    if (user.is_trial) {
        planSuffix += ' · Trial';
    }
    return {
        user_obj: user,
        match_index: matchIndex,
        match_rank: matchRank,
        role,
        plan_suffix: planSuffix,
        is_suspended: isSuspended(user),
        org_count: orgCount,
    };
}

export function searchHaystack(user) {
    return [
        user.username,
        user.email,
        user.first_name,
        user.last_name,
        user.organization,
        user.organization_internal_name,
        user.organization_id,
        user.sfdc_account_id,
    ]
        .filter(Boolean)
        .join(' ')
        .toUpperCase();
}

export function matchGroups(groups, searchString) {
    const term = (searchString || '').trim().toUpperCase();
    if (!term) {
        return [];
    }
    const matches = [];
    groups.forEach(({ user, rows, org_count: orgCount = 1 }) => {
        const matchIndex = rows
            .map(row => searchHaystack(row))
            .join(' ')
            .indexOf(term);
        if (matchIndex >= 0) {
            const matchRank = Math.min(...rows.map(row => orgMatchRank(row, term)));
            matches.push(mapUserToOption(user, matchIndex, matchRank, orgCount));
        }
    });
    return sortBy(matches, ['match_rank', option => roleRank(option.role), 'match_index']);
}

export function matchUsers(users, searchString) {
    return matchGroups(
        users.map(user => ({ user, rows: [user] })),
        searchString
    );
}

export function hasChipFilters(filters) {
    return Boolean(filters.tierfilter || filters.rolefilter || filters.showAnalyticsOnly);
}

export function shouldIncludeUser(user, filters) {
    if (filters.showAnalyticsOnly && user.is_unified) {
        return false;
    }
    if (filters.rolefilter && getRoleKey(user) !== filters.rolefilter) {
        return false;
    }
    if (filters.tierfilter && getTierKey(user) !== filters.tierfilter) {
        return false;
    }
    if (!filters.showDevAccounts && user.is_dev_account) {
        return false;
    }
    return true;
}

function usernameSortKey(user) {
    return String(user.username || '')
        .trim()
        .toLowerCase();
}

function usernameSortRank(user) {
    return /^[a-z]/.test(usernameSortKey(user)) ? 0 : 1;
}

function dedupeByUserId(users) {
    const byId = new Map();
    users.forEach((user, index) => {
        const key = user.user_id === null || user.user_id === undefined ? `__row_${index}` : user.user_id;
        const entry = byId.get(key);
        if (entry) {
            entry.org_count += 1;
            entry.rows.push(user);
        } else {
            byId.set(key, { user, key, org_count: 1, rows: [user] });
        }
    });
    return [...byId.values()];
}

export function computeVisibleUsers({ allUsers, searchString, filters, limit = RESULTS_LIMIT }) {
    const searching = Boolean((searchString || '').trim());
    const entries = dedupeByUserId(allUsers || []);
    const included = entries.filter(entry => shouldIncludeUser(entry.user, filters));

    let options;
    if (searching) {
        options = matchGroups(included, searchString);
    } else {
        options = sortBy(
            included.map((entry, index) => mapUserToOption(entry.user, index, MATCH_OTHER, entry.org_count)),
            [option => usernameSortRank(option.user_obj), option => usernameSortKey(option.user_obj)]
        );
    }

    return {
        options: options.slice(0, limit),
        total: options.length,
        uniqueTotal: entries.length,
        searching,
    };
}

export function isCurrentUser(option, currentUserId) {
    if (!option || currentUserId === null || currentUserId === undefined) {
        return false;
    }
    return Boolean(option.user_obj) && option.user_obj.user_id === currentUserId;
}

export function canImpersonate(option, currentUserId) {
    if (!option || option.org_count > 1 || isCurrentUser(option, currentUserId)) {
        return false;
    }
    return true;
}

export function impersonationBlockedReason(option, currentUserId) {
    if (isCurrentUser(option, currentUserId)) {
        return { key: 'BLOCKED_CURRENT_USER' };
    }
    if (option && option.org_count > 1) {
        return { key: 'BLOCKED_MULTI_ORG', count: option.org_count };
    }
    return null;
}

export function formatUserSubtitle(user) {
    if (!user) {
        return '';
    }
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return [name, user.organization].filter(Boolean).join(' · ');
}

export function formatArr(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const amount = Number(value);
    if (Number.isNaN(amount)) {
        return null;
    }
    return `$${amount.toLocaleString()}`;
}

export function buildSalesforceAccountUrl(sfdcAccountId) {
    if (!sfdcAccountId) {
        return null;
    }
    return `https://singular.lightning.force.com/lightning/r/Account/${encodeURIComponent(sfdcAccountId)}/view`;
}

export function buildUserDetails(option) {
    if (!option) {
        return [];
    }
    if (option.org_count > 1) {
        return [{ labelKey: 'ORGANIZATION', valueKey: 'FIELDS.MULTIPLE_ORGS', valueData: { count: option.org_count } }];
    }
    const user = option.user_obj;
    const isPartnerOrAgency = Boolean(user.is_partner || user.is_agency);
    const { organization: orgName, organization_internal_name: orgInternalName } = user;
    const organization = orgName && orgInternalName ? `${orgName} (${orgInternalName})` : orgName || orgInternalName;

    const rows = [
        { labelKey: 'ORGANIZATION', value: organization },
        { labelKey: 'ORG_ID', value: user.organization_id },
    ];
    if (user.is_agency) {
        rows.push({ labelKey: 'AGENCY_ID', value: user.agency_id });
    }
    if (!isPartnerOrAgency) {
        rows.push({
            labelKey: 'SFDC_ID',
            value: user.sfdc_account_id,
            href: buildSalesforceAccountUrl(user.sfdc_account_id),
        });
        rows.push({ labelKey: 'PLAN', tier: user.organization_tier, valueSuffix: option.plan_suffix });
        rows.push({ labelKey: 'REGION', value: user.organization_region });
        rows.push({ labelKey: 'ARR', value: formatArr(user.sales_force_arr), valueKey: 'FIELDS.UNKNOWN' });
    }
    rows.push({ labelKey: 'ROLE', value: option.role });
    if (!isPartnerOrAgency) {
        rows.push({
            labelKey: 'CUSTOMER_TYPE',
            valueKey: user.is_unified ? 'FIELDS.ATTRIBUTION_UNIFIED' : 'FIELDS.ANALYTICS_ONLY',
        });
    }

    return rows.filter(row => {
        const hasValue = row.value !== null && row.value !== undefined && row.value !== '';
        return hasValue || Boolean(row.valueKey) || Boolean(row.tier);
    });
}

export function buildImpersonateUrl(username, hash) {
    const hashPath = (hash || '').replace(/^#/, '');
    return `/?user=${encodeURIComponent(username)}#${hashPath}`;
}
